import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import CloseIcon from "@mui/icons-material/Close";
import { scanDirectoryFromPicker } from "../../utils/recursiveDirectoryWalk";
import {
  compareWithAppFolders,
  type CompareWithAppFoldersResult,
} from "../../utils/compareWithAppFolders";
import { type Folder } from "./document-list";
import CompareDirectoryTree from "./compare-directory-tree";

interface CompareDirectoryDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Folder tree to compare against. When navigated inside a folder this is the
   * currently selected folder's sub-folders (scoped), otherwise the full IATA tree.
   */
  appFolders: Folder[];
  /** Files that live directly in the currently navigated folder (scope root). */
  scopeDocuments?: { fileName: string }[];
  /** Name of the currently navigated folder, used for labels. Null at the root. */
  scopeLabel?: string | null;
  tailNumber?: string | null;
}

const dialogPaperSx = {
  backgroundColor: "#111",
  color: "#fff",
  border: "1px solid #333",
};

const dialogTitleSx = {
  color: "hsl(19 101% 51% / 1)",
};

const outlineButtonSx = {
  color: "hsl(19 101% 51% / 1)",
  borderColor: "hsl(19 101% 51% / 1)",
  textTransform: "none",
  fontWeight: 700,
};

type CompareView = "missing" | "onlyInApp";

const CompareDirectoryDialog: React.FC<CompareDirectoryDialogProps> = ({
  open,
  onClose,
  appFolders,
  scopeDocuments,
  scopeLabel,
  tailNumber,
}) => {
  const [compareResult, setCompareResult] = useState<CompareWithAppFoldersResult | null>(null);
  const [compareView, setCompareView] = useState<CompareView>("missing");
  const [isScanning, setIsScanning] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedFolderName, setSelectedFolderName] = useState<string | null>(null);

  const normalizedTail = tailNumber?.trim() ?? "";
  const scopeRootDocuments = scopeDocuments ?? [];
  const normalizedScopeLabel = scopeLabel?.trim() ?? "";
  const hasAppFolders = appFolders.length > 0 || scopeRootDocuments.length > 0;

  const displayedEntries = useMemo(() => {
    if (!compareResult) {
      return [];
    }
    return compareView === "missing" ? compareResult.missingInApp : compareResult.onlyInApp;
  }, [compareResult, compareView]);

  const displayedSummary = useMemo(() => {
    const directoryCount = displayedEntries.filter((entry) => entry.kind === "directory").length;
    const fileCount = displayedEntries.filter((entry) => entry.kind === "file").length;
    return { directoryCount, fileCount };
  }, [displayedEntries]);

  const handleClose = () => {
    if (isScanning) {
      return;
    }
    onClose();
  };

  const handleSelectFolder = async () => {
    if (!hasAppFolders) {
      setErrorMessage("Run a document search first so the IATA folder structure is loaded.");
      return;
    }

    if (isScanning) {
      return;
    }

    setErrorMessage(null);
    setCompareResult(null);
    setStatusMessage("Choose a folder in the dialog that opens…");
    setIsScanning(true);

    try {
      const scan = await scanDirectoryFromPicker();
      setSelectedFolderName(scan.resolvedRootPath);

      const result = compareWithAppFolders(
        scan.entries,
        appFolders,
        scan.resolvedRootPath,
        scopeRootDocuments
      );
      setCompareResult(result);
      setCompareView("missing");

      const scopePart = normalizedScopeLabel ? ` in "${normalizedScopeLabel}"` : "";
      const tailPart = normalizedTail ? ` for ${normalizedTail}` : "";
      const tailLabel = `${scopePart}${tailPart}`;
      if (result.localFileCount === 0) {
        setStatusMessage(
          `Folder "${scan.resolvedRootPath}" was read, but no files were found. Pick a folder that contains documents, or check the browser's read permission.`
        );
      } else if (result.missingFileCount === 0) {
        setStatusMessage(`All ${result.localFileCount} local file(s) match files in the app${tailLabel}.`);
      } else {
        setStatusMessage(
          `${result.missingFileCount} of ${result.localFileCount} local file(s) are not in the app folders${tailLabel}.`
        );
      }
    } catch (error) {
      setCompareResult(null);
      setSelectedFolderName(null);
      if (error instanceof DOMException && error.name === "AbortError") {
        setStatusMessage("Folder selection was cancelled.");
        return;
      }
      const message =
        error instanceof Error ? error.message : "Unable to read the selected directory.";
      setErrorMessage(message);
      setStatusMessage(null);
    } finally {
      setIsScanning(false);
    }
  };

  React.useEffect(() => {
    if (!open) {
      setCompareResult(null);
      setSelectedFolderName(null);
      setStatusMessage(null);
      setErrorMessage(null);
      setCompareView("missing");
      setIsScanning(false);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle
        sx={{
          ...dialogTitleSx,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        Compare with app folders
        <IconButton
          aria-label="close"
          onClick={handleClose}
          disabled={isScanning}
          sx={{ color: "#ccc" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "#aaa", mb: 2 }}>
          Select a local folder to compare against{" "}
          {normalizedScopeLabel ? (
            <>
              the <strong>{normalizedScopeLabel}</strong> folder
            </>
          ) : (
            "the IATA folder structure"
          )}{" "}
          shown in this view (from your document search). Files listed under{" "}
          <strong>Missing in app</strong> are in your folder but not in the app&apos;s records.
          {normalizedTail ? (
            <>
              {" "}
              Aircraft: <strong>{normalizedTail}</strong>.
            </>
          ) : null}
        </Typography>

        {!hasAppFolders && (
          <Typography variant="body2" sx={{ color: "hsl(19 101% 51% / 1)", mb: 2 }}>
            Search for an aircraft and open its folders before comparing.
          </Typography>
        )}

        <Button
          variant="outlined"
          startIcon={
            isScanning ? (
              <CircularProgress size={18} sx={{ color: "hsl(19 101% 51% / 1)" }} />
            ) : (
              <FolderOpenIcon />
            )
          }
          onClick={() => void handleSelectFolder()}
          disabled={isScanning || !hasAppFolders}
          sx={outlineButtonSx}
        >
          Select folder to compare
        </Button>

        {statusMessage && (
          <Typography variant="body2" sx={{ color: "#9ccc65", mt: 1.5 }}>
            {statusMessage}
          </Typography>
        )}
        {errorMessage && (
          <Typography variant="body2" sx={{ color: "hsl(19 101% 51% / 1)", mt: 1 }}>
            {errorMessage}
          </Typography>
        )}

        {compareResult && (
          <Box sx={{ mt: 2 }}>
            {selectedFolderName && (
              <Typography variant="body2" sx={{ color: "#ccc", mb: 1 }}>
                Local folder: {selectedFolderName} · App has {compareResult.appFileCount} file(s)
              </Typography>
            )}

            <Tabs
              value={compareView}
              onChange={(_, value: CompareView) => setCompareView(value)}
              sx={{
                minHeight: 36,
                mb: 1,
                "& .MuiTab-root": {
                  color: "#aaa",
                  textTransform: "none",
                  minHeight: 36,
                  py: 0.5,
                },
                "& .Mui-selected": { color: "hsl(19 101% 51% / 1)" },
                "& .MuiTabs-indicator": { backgroundColor: "hsl(19 101% 51% / 1)" },
              }}
            >
              <Tab
                value="missing"
                label={`Missing in app (${compareResult.missingFileCount})`}
              />
              <Tab
                value="onlyInApp"
                label={`Only in app (${compareResult.onlyInAppFileCount})`}
              />
            </Tabs>

            {displayedEntries.length > 0 ? (
              <>
                <Typography variant="body2" sx={{ color: "#ccc", mb: 1 }}>
                  {displayedSummary.directoryCount} folders, {displayedSummary.fileCount} files
                </Typography>
                <CompareDirectoryTree
                  entries={displayedEntries}
                  highlightDiff={compareView === "missing"}
                />
              </>
            ) : (
              <Typography variant="body2" sx={{ color: "#9ccc65" }}>
                {compareView === "missing"
                  ? "No differences — every local file exists in the app folders."
                  : "No files found only in the app."}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isScanning} sx={{ color: "#ccc", textTransform: "none" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompareDirectoryDialog;
